import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useFloatingWindowsStore } from '@/state/floatingWindows.store';
import { TENANT_CHANGE_EVENT } from '@/utils/storage/tenantScoping.util';

import { FloatingWindowFrame } from './FloatingWindowFrame';
import { computeZIndex } from './floatingWindowGeometry';
import { MinimizedTaskbar } from './MinimizedTaskbar';

/**
 * Renders every open FloatingWindow. Mount ONCE, above the router (sibling of
 * `<Outlet/>`), so windows and their live bodies survive route/drawer unmount.
 *
 * - Non-modal: the host root is `fixed inset-0 pointer-events-none`; only the
 *   window frames re-enable pointer events, so the app behind stays interactive.
 * - The root's `z-[150]` puts the whole window layer above app drawers/dialogs
 *   (z-100) and below Radix `dropdown-menu`/`select` (z-200) and `alert-dialog`
 *   (z-10000); intra-layer ordering comes from each frame's derived `zIndex`.
 * - Windows render in STABLE insertion order (instances object key order) keyed
 *   by id — focus never reorders the DOM (which would reload in-body iframes/
 *   media); stacking is driven purely by the inline `zIndex`.
 */
export function FloatingWindowHost() {
  const instances = useFloatingWindowsStore(s => s.instances);
  const focusOrder = useFloatingWindowsStore(s => s.focusOrder);
  const closeAllWindows = useFloatingWindowsStore(s => s.closeAllWindows);
  const setPosition = useFloatingWindowsStore(s => s.setPosition);
  const setSize = useFloatingWindowsStore(s => s.setSize);

  // Tenant switch (impersonation start/stop) must not leak a window across
  // tenants — mirror the KeyedInsights remount (ENG-3422) by closing all.
  useEffect(() => {
    window.addEventListener(TENANT_CHANGE_EVENT, closeAllWindows);
    return () =>
      window.removeEventListener(TENANT_CHANGE_EVENT, closeAllWindows);
  }, [closeAllWindows]);

  // Re-clamp all windows back into the viewport on browser resize. setSize/
  // setPosition clamp internally; passing current values is a no-op unless the
  // window now overflows.
  useEffect(() => {
    const onResize = () => {
      const current = useFloatingWindowsStore.getState().instances;
      for (const instance of Object.values(current)) {
        // Maximized windows are edge-anchored via CSS and track the viewport on
        // their own — re-clamping would persist a stale viewport-sized value.
        if (instance.maximized) continue;
        setSize(instance.id, instance.size);
        setPosition(instance.id, instance.position);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setPosition, setSize]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="floating-window-host"
      className="pointer-events-none fixed inset-0 z-[150]"
    >
      <AnimatePresence>
        {Object.values(instances).map(instance => (
          <FloatingWindowFrame
            key={instance.id}
            instance={instance}
            zIndex={computeZIndex(focusOrder, instance.id)}
          >
            {instance.children}
          </FloatingWindowFrame>
        ))}
      </AnimatePresence>
      <MinimizedTaskbar />
    </div>,
    document.body
  );
}
